import React from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { tokens } from "../../Common/designTokens";
import {
  renderFilterField,
  getRangeError,
} from "../MAInventoryComponents/watchlistFilterFields";
import { useSecMasterSearch, SEC_MASTER_SEARCH_TABS, SEC_MASTER_FILTER_FIELDS } from "./useSecMasterSearchHooks";

// Range fields whose min/max pair must be validated before Apply is allowed.
const RANGE_ERROR_CHECKS = [
  ["couponRange", "couponMin", "couponMax"],
  ["maturityRanges", "maturityMin", "maturityMax"],
  ["price", "priceMin", "priceMax"],
  ["yield", "yieldMin", "yieldMax"],
];

const SecMasterSearch = () => {
  const {
    tabValue,
    setTabValue,
    activeBondType,
    filterData,
    filterOptions,
    setFilterField,
    criteriaCount,
    isCriteriaLoading,
    countError,
    optionsError,
    handleApply,
    handleClear,
    handleGoBack,
  } = useSecMasterSearch();

  const fields = SEC_MASTER_FILTER_FIELDS[activeBondType] || [];

  const rangeErrors = {};
  RANGE_ERROR_CHECKS.forEach(([key, minName, maxName]) => {
    rangeErrors[key] = getRangeError(filterData, minName, maxName);
  });
  const hasRangeError = fields.some((field) => rangeErrors[field]);

  const isCountLoading = isCriteriaLoading || criteriaCount === null;
  const disableApply = hasRangeError || criteriaCount === 0;

  const renderCriteriaCount = () => {
    if (isCountLoading) {
      return <CircularProgress size={20} sx={{ ml: 1 }} />;
    }
    if (criteriaCount === 0) {
      return (
        <Typography component="span" color="error" sx={{ fontSize: "inherit", fontWeight: "inherit" }}>
          None Available
        </Typography>
      );
    }
    return criteriaCount.toLocaleString("en-US");
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton onClick={handleGoBack} sx={{ mr: 1, p: 0.5 }} aria-label="back">
          <ArrowBackIcon sx={{ fontSize: 24 }} />
        </IconButton>
        <Typography
          variant="h6"
          sx={{
            fontSize: { xs: "1rem", sm: "1.25rem" },
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          Search Securities
        </Typography>
      </Box>

      {(optionsError || countError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {optionsError || countError}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabValue} onChange={(_e, newValue) => setTabValue(newValue)}>
          {SEC_MASTER_SEARCH_TABS.map((tab, index) => (
            <Tab key={tab.bondType} label={tab.label} value={index} />
          ))}
          {/* <Tab label="CDs" disabled /> */}
          {/* <Tab label="Agency/GSEs" disabled /> */}
        </Tabs>
      </Box>

      <Box sx={{ border: 1, borderColor: "divider", p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Key Search Criteria: {renderCriteriaCount()}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 2,
            "& > *": { minWidth: 0 },
          }}
        >
          {fields.map((field) =>
            renderFilterField(field, filterData, setFilterField, {
              ...filterOptions,
              rangeErrors,
            })
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
          <Button
            variant="outlined"
            onClick={handleClear}
            sx={{ borderRadius: "8px", height: 40, width: 160, textTransform: "none" }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={disableApply}
            sx={{
              borderRadius: "8px",
              height: 40,
              width: 160,
              fontWeight: "bold",
              textTransform: "none",
              bgcolor: disableApply ? tokens.colors.defaultGridHeaderBackground : tokens.palette.primary,
              color: disableApply ? tokens.colors.disabledFontColour : tokens.colors.white,
              opacity: disableApply ? 0.6 : 1,
              "&:hover": {
                backgroundColor: disableApply
                  ? tokens.colors.defaultGridRowColor
                  : tokens.palette.primaryA80,
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SecMasterSearch;
